import { getNpsTemplateMessages } from '@/dashboards-v2/widgets/nps-widget/nps-widget-messages';
import { i18n_t as $t } from '@/utils/i18n';
import { NPS_COLOR_MAP } from '@/common/chart/chart-colors.js';

export const getChannelOptionsMap = () => ({
    inAppPlusEmail: {
        label: $t('npsConstants.In-app + Email'),
        value: 'inAppPlusEmail',
    },
    inAppOnly: {
        label: $t('npsConstants.In-app only'),
        value: 'inAppOnly',
    },
    emailOnly: {
        label: $t('npsConstants.Email only'),
        value: 'emailOnly',
    },
});

// NPS polls are the combination of two polls, the actual NPS number and
// the follow-up response. We currently have no way of connecting these two polls
// other than by the proximity of the `browserTime` value on each pollEvent.
// In aggregations we match a visitor's follow-up response to whichever nps response
// was made in the same 1000ms.
export const HALF_RANGE_TO_MATCH_NPS_TO_FOLLOWUP = 500;

export const NPS_SCATTER_PLOT_LIMIT = 1000;

export const npsFilters = {
    textResponses: {
        value: 'textResponses',
        get label () {
            return $t('npsConstants.Text responses');
        },
    },
    blankResponses: {
        value: 'blankResponses',
        get label () {
            return $t('npsConstants.Blank responses');
        },
    },
    noThemeResponses: {
        value: 'noThemeResponses',
        get label () {
            return $t('npsConstants.No Theme');
        },
    },
    rating: [
        {
            id: 'promoters',
            get responseGroup () {
                return $t('npsConstants.Promoters');
            },
            items: [
                { id: '10', label: '10' },
                { id: '9', label: '9' },
            ],
            type: 'metaGroup',
        },
        {
            id: 'passives',
            get responseGroup () {
                return $t('npsConstants.Passives');
            },
            items: [
                { id: '8', label: '8' },
                { id: '7', label: '7' },
            ],
            type: 'metaGroup',
        },
        {
            id: 'detractors',
            get responseGroup () {
                return $t('npsConstants.Detractors');
            },
            items: [
                { id: '6', label: '6' },
                { id: '5', label: '5' },
                { id: '4', label: '4' },
                { id: '3', label: '3' },
                { id: '2', label: '2' },
                { id: '1', label: '1' },
                { id: '0', label: '0' },
            ],
            type: 'metaGroup',
        },
    ],
};

export const ratingsFilterEmptyOption = {
    get label () {
        return $t('npsConstants.All Ratings');
    },
};

export const npsThemesModalSettings = {
    edit: {
        get modalTitle () {
            return $t('npsConstants.Rename theme');
        },
        get label () {
            return $t('npsConstants.Rename');
        },
        get confirmBtnLabel () {
            return $t('npsConstants.Save NPS theme');
        },
        get description () {
            return $t('npsConstants.theme name will change for all associated responses.');
        },
    },
    create: {
        get modalTitle () {
            return $t('npsConstants.Add theme name');
        },
        get label () {
            return $t('npsConstants.Type a new NPS theme');
        },
        get confirmBtnLabel () {
            return $t('npsConstants.Create NPS theme');
        },
    },
    delete: {
        get modalTitle () {
            return $t('npsConstants.Delete theme');
        },
        get confirmBtnLabel () {
            return $t('npsConstants.Delete NPS theme');
        },
        get subTitle () {
            return $t('npsConstants.deleteConfirmationSubtitle', {
                strongOpen: '<strong>',
                strongClose: '</strong>',
            });
        },
        get description () {
            return $t('npsConstants.This action cannot be undone.');
        },
        get warning () {
            return $t('npsConstants.deleteThemeWarning', {
                lineBreak: '<br>',
                strongOpen: '<strong>',
                strongClose: '</strong>',
            });
        },
    },
};

export const npsThemesDropdownActions = [
    {
        type: 'edit',
        get tooltip () {
            return $t('common.Edit');
        },
        icon: {
            type: 'edit',
        },
    },
    {
        type: 'delete',
        get tooltip () {
            return $t('common.Delete');
        },
        icon: {
            type: 'trash-2',
        },
    },
];

export const ML_SPECIAL_NPS_THEME_IDS = {
    likesDislikesTheme: 'Likes / Dislikes',
    generalNpsTheme: 'Unassigned',
};

export const generalNpsThemeDisplayName = () => $t('npsConstants.Others');

export const pendingThemeOption = {
    id: '__pending__',
    get label () {
        return $t('npsConstants.Finding the best theme…');
    },
};

export const widgetMessageMap = {
    ...getNpsTemplateMessages(),
    noThemeSelected: {
        get text () {
            return $t('npsConstants.No theme selected');
        },
        get subtext () {
            return $t('npsConstants.Please select a theme to see themes distribution.');
        },
    },
    noResponsesToSelectedTheme: {
        get text () {
            return $t('npsConstants.No Theme Responses');
        },
        get subtext () {
            return $t('npsConstants.Theme has no associated responses.');
        },
    },
    deletedTheme: {
        get text () {
            return $t('npsConstants.No Theme');
        },
        get subtext () {
            return $t('npsConstants.Theme was deleted.');
        },
    },
};

export const NPS_GPT_SUMMARY_STATUS = {
    LOADING: 'loading',
    RESOLVED: 'resolved',
    FAILED: 'failed',
    INSUFFICIENT_RESPONSES: 'insufficientResponses',
};

export const gptSummaryMinResponsesLimit = 5;

export const gptSummaryMaxResponsesLimit = 150;

export const gptSummaryEmptyText = {
    get [NPS_GPT_SUMMARY_STATUS.INSUFFICIENT_RESPONSES] () {
        return $t(
            'npsConstants.Insufficient responses found.\nTheme summary generation requires\na minimum of 5 responses.',
        );
    },
    get [NPS_GPT_SUMMARY_STATUS.FAILED] () {
        return $t('npsConstants.Response summaries are unavailable at the moment.\nCheck again later.');
    },
};

export const RESPONSE_GROUPS = {
    promoter: 'Promoter',
    passive: 'Passive',
    detractor: 'Detractor',
};

export const RESPONSE_GROUP_COLOR = {
    [RESPONSE_GROUPS.promoter]: NPS_COLOR_MAP.promoters,
    [RESPONSE_GROUPS.passive]: NPS_COLOR_MAP.passive,
    [RESPONSE_GROUPS.detractor]: NPS_COLOR_MAP.detractors,
};

export const NPS_THEME_LABEL_MAX_CHARS = 25;

export const responsesSortOptions = [
    {
        id: 'by_date_desc',
        prop: 'browserTime',
        dir: 'desc',
        get label () {
            return $t('npsConstants.By date (newest to oldest)');
        },
    },
    {
        id: 'by_date_asc',
        prop: 'browserTime',
        dir: 'asc',
        get label () {
            return $t('npsConstants.By date (oldest to newest)');
        },
    },
    {
        id: 'by_rating_desc',
        prop: 'rating',
        dir: 'desc',
        get label () {
            return $t('npsConstants.By rating (highest to lowest)');
        },
    },
    {
        id: 'by_rating_asc',
        prop: 'rating',
        dir: 'asc',
        get label () {
            return $t('npsConstants.By rating (lowest to highest)');
        },
    },
];

export const responsesGroupFilterOptions = [
    {
        id: RESPONSE_GROUPS.promoter,
        get label () {
            return $t('npsConstants.Promoters');
        },
        groupColor: RESPONSE_GROUP_COLOR.Promoter,
    },
    {
        id: RESPONSE_GROUPS.passive,
        get label () {
            return $t('npsConstants.Passives');
        },
        groupColor: RESPONSE_GROUP_COLOR.Passive,
    },
    {
        id: RESPONSE_GROUPS.detractor,
        get label () {
            return $t('npsConstants.Detractors');
        },
        groupColor: RESPONSE_GROUP_COLOR.Detractor,
    },
];

export const themeNameValidationFactory = (initialValue = null) => {
    /* sometime we have initial value which is longer then max length allowed.
    In this case it is still consider to be valid */
    return [
        {
            validate: (value) => value.trim() !== '',
            get message () {
                return $t('npsConstants.Title is required');
            },
        },
        {
            validate: (value) => value.length <= NPS_THEME_LABEL_MAX_CHARS || value === initialValue,
            get message () {
                return $t('npsConstants.Title cannot be longer than {maxChars} characters.', {
                    maxChars: NPS_THEME_LABEL_MAX_CHARS,
                });
            },
        },
    ];
};

export const EMAIL_FREQUENCY = {
    MONTHLY: 'monthly',
    WEEKLY: 'weekly',
};

export const emailSummarySettings = {
    notificationType: 'npssummaryemail',
    targetType: 'npssummary',
    urlFlag: '?sendNPSSummaryEmail=true',
};
